import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useNoizaMetrics, DateRange } from '../../lib/noiza-metrics-hooks';
import { formatCurrency } from '../../lib/utils';
import { TrendingUpIcon, ShoppingCartIcon, ReceiptIcon, StoreIcon, UsersIcon, UserCheckIcon, ArrowLeftIcon, ActivityIcon, RefreshCwIcon, Loader2Icon, CalendarIcon, FilterIcon, ClockIcon, AwardIcon, AlertTriangleIcon, ChevronDownIcon, FileTextIcon } from 'lucide-react';
// ─── Helpers ───────────────────────────────────────────────
function formatCompactCurrency(value: number): string {
  if (value >= 1000000) return `₡${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `₡${(value / 1000).toFixed(0)}k`;
  return formatCurrency(value);
}
function formatDayLabel(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${d} ${months[m - 1]}`;
  } catch {
    return dateStr;
  }
}
function normalizeChannel(ch: string): string {
  const lower = (ch || '').toLowerCase();
  if (lower.includes('qr') || lower.includes('self')) return 'QR';
  if (lower.includes('waiter') || lower.includes('mesero')) return 'Mesero';
  if (lower.includes('web') || lower.includes('online')) return 'Web';
  if (lower.includes('caja') || lower.includes('cashier')) return 'Caja';
  return ch || 'Otro';
}
const CHANNEL_COLORS: Record<string, string> = {
  QR: '#10b981',
  Mesero: '#6366f1',
  Web: '#f59e0b',
  Caja: '#8b5cf6',
  Otro: '#6b7280'
};
function getDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
}
function getToday(): string {
  return new Date().toISOString().split('T')[0];
}
function getFirstDayOfMonth(): string {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
}
// ─── Dark Tooltip ──────────────────────────────────────────
function DarkTooltip({
  active,
  payload,
  label,
  isCurrency
}: any) {
  if (!active || !payload?.length) return null;
  return <div className="bg-white/10 backdrop-blur-xl border border-white/10 text-white px-4 py-3 rounded-xl shadow-2xl text-sm z-50" data-id="element-3360">
      <p className="font-medium text-white/60 mb-1.5 text-xs uppercase tracking-wider" data-id="element-3361">
        {label}
      </p>
      {payload.map((entry: any, i: number) => <p key={i} className="flex items-center gap-2" data-id="element-3362">
          <span className="w-2 h-2 rounded-full" style={{
        backgroundColor: entry.color || entry.fill
      }} data-id="element-3363" />
          <span className="text-white/50" data-id="element-3364">{entry.name}:</span>
          <span className="font-semibold text-white" data-id="element-3365">
            {isCurrency ? formatCurrency(Number(entry.value ?? 0)) : Number(entry.value ?? 0).toLocaleString()}
          </span>
        </p>)}
    </div>;
}
// ─── KPI Card ──────────────────────────────────────────────
interface KpiCardProps {
  label: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  accentColor: string;
}
function KpiCard({
  label,
  value,
  subtitle,
  icon,
  accentColor
}: KpiCardProps) {
  return <div className="relative overflow-hidden rounded-2xl bg-[#12121a] border border-white/[0.06] p-5 group" data-id="element-3366">
      <div className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-[0.07] -translate-y-8 translate-x-8`} style={{
      backgroundColor: accentColor
    }} data-id="element-3367" />
      <div className="relative" data-id="element-3368">
        <div className="flex items-center justify-between mb-4" data-id="element-3369">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
          backgroundColor: `${accentColor}15`
        }} data-id="element-3370">
            {icon}
          </div>
        </div>
        <p className="text-[13px] font-medium text-white/40 uppercase tracking-wider" data-id="element-3371">
          {label}
        </p>
        <p className="text-3xl font-bold text-white mt-1 tracking-tight" data-id="element-3372">
          {value}
        </p>
        {subtitle && <p className="text-xs text-white/30 mt-1" data-id="element-3373">{subtitle}</p>}
      </div>
    </div>;
}
// ─── Main Page ─────────────────────────────────────────────
export function NoizaMetricsPage() {
  const [restaurantId, setRestaurantId] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange>({
    from: getDaysAgo(30),
    to: getToday()
  });
  const [datePreset, setDatePreset] = useState<string>('30d');
  const {
    data,
    loading,
    error,
    refetch,
    restaurants
  } = useNoizaMetrics(restaurantId, dateRange);
  // Set default restaurant to Capricho de Canela if available and not yet set
  useEffect(() => {
    if (restaurants.length > 0 && restaurantId === 'all') {
      const capricho = restaurants.find(r => r.slug === 'capricho-de-canela');
      if (capricho) {
        setRestaurantId(capricho.id);
      }
    }
  }, [restaurants]);
  const handleDatePreset = (preset: string) => {
    setDatePreset(preset);
    const to = getToday();
    let from = to;
    if (preset === 'all') from = '2020-01-01';else if (preset === 'today') from = to;else if (preset === '7d') from = getDaysAgo(7);else if (preset === '30d') from = getDaysAgo(30);else if (preset === 'month') from = getFirstDayOfMonth();
    setDateRange({
      from,
      to
    });
  };
  // Derived Data for Charts
  const dailyChartData = useMemo(() => {
    const source = data.salesDaily.length > 0 ? data.salesDaily : data.gmvDaily;
    // Aggregate by day if multiple restaurants
    const aggregated = source.reduce((acc: any, curr: any) => {
      const day = curr.day;
      if (!acc[day]) acc[day] = {
        day,
        label: formatDayLabel(day),
        gmv: 0,
        orders: 0
      };
      acc[day].gmv += curr.gmv;
      acc[day].orders += curr.orders;
      return acc;
    }, {});
    return Object.values(aggregated).sort((a: any, b: any) => a.day.localeCompare(b.day));
  }, [data.salesDaily, data.gmvDaily]);
  const hourlyChartData = useMemo(() => {
    const aggregated = data.salesHourly.reduce((acc: any, curr: any) => {
      const hour = curr.hour;
      if (!acc[hour]) acc[hour] = {
        hour,
        label: `${hour}:00`,
        gmv: 0,
        orders: 0
      };
      acc[hour].gmv += curr.gmv;
      acc[hour].orders += curr.orders;
      return acc;
    }, {});
    return Object.values(aggregated).sort((a: any, b: any) => Number(a.hour) - Number(b.hour));
  }, [data.salesHourly]);
  const channelChartData = useMemo(() => {
    return data.channelDistribution.map(d => ({
      name: normalizeChannel(d.channel),
      value: d.orders,
      pct: d.pct,
      color: CHANNEL_COLORS[normalizeChannel(d.channel)] || '#6b7280'
    })).filter(d => d.value > 0);
  }, [data.channelDistribution]);
  const totalChannelOrders = channelChartData.reduce((sum, d) => sum + d.value, 0);
  const topRestaurantsChartData = useMemo(() => {
    return data.gmvByRestaurant.slice(0, 5).map(r => ({
      name: String(r.restaurant_name || '').length > 15 ? String(r.restaurant_name || '').substring(0, 15) + '...' : String(r.restaurant_name || ''),
      fullName: r.restaurant_name,
      gmv: r.gmv
    }));
  }, [data.gmvByRestaurant]);
  const topProductsChartData = useMemo(() => {
    return data.topProducts.slice(0, 5).map(p => ({
      name: String(p.product_name || '').length > 15 ? String(p.product_name || '').substring(0, 15) + '...' : String(p.product_name || ''),
      fullName: p.product_name,
      revenue: p.revenue,
      quantity: p.quantity
    }));
  }, [data.topProducts]);
  // Insights
  const insights = useMemo(() => {
    let bestDay = {
      day: '-',
      val: 0
    };
    let worstDay = {
      day: '-',
      val: Infinity
    };
    dailyChartData.forEach((d: any) => {
      if (d.gmv > bestDay.val) bestDay = {
        day: d.label,
        val: d.gmv
      };
      if (d.gmv < worstDay.val && d.gmv > 0) worstDay = {
        day: d.label,
        val: d.gmv
      };
    });
    let bestHour = {
      hour: '-',
      val: 0
    };
    let worstHour = {
      hour: '-',
      val: Infinity
    };
    hourlyChartData.forEach((d: any) => {
      if (d.gmv > bestHour.val) bestHour = {
        hour: d.label,
        val: d.gmv
      };
      if (d.gmv < worstHour.val && d.gmv > 0) worstHour = {
        hour: d.label,
        val: d.gmv
      };
    });
    let bestRest = data.gmvByRestaurant[0];
    let worstRest = data.gmvByRestaurant[data.gmvByRestaurant.length - 1];
    return {
      bestDay,
      worstDay,
      bestHour,
      worstHour,
      bestRest,
      worstRest
    };
  }, [dailyChartData, hourlyChartData, data.gmvByRestaurant]);
  return <div className="min-h-screen bg-[#0a0a0f] text-white pb-20" data-id="element-3374">
      {/* ─── Header ─────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/[0.06]" data-id="element-3375">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-3376">
          <div className="flex items-center gap-4" data-id="element-3377">
            <Link to="/admin/enrollment" className="w-9 h-9 rounded-xl bg-white/[0.06] flex items-center justify-center hover:bg-white/[0.1] transition-colors" data-id="element-3378">
              <ArrowLeftIcon className="w-4 h-4 text-white/60" data-id="element-3379" />
            </Link>
            <div data-id="element-3380">
              <div className="flex items-center gap-2.5" data-id="element-3381">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20" data-id="element-3382">
                  <ActivityIcon className="w-4 h-4 text-white" data-id="element-3383" />
                </div>
                <h1 className="text-lg font-bold tracking-tight" data-id="element-3384">NOIZA</h1>
                <span className="text-xs font-medium text-white/30 bg-white/[0.06] px-2 py-0.5 rounded-md" data-id="element-3385">
                  Executive Dashboard
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-3386">
            <button onClick={refetch} disabled={loading} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] transition-colors text-sm text-white/60 hover:text-white/80 disabled:opacity-50" data-id="element-3387">
              {loading ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-3388" /> : <RefreshCwIcon className="w-4 h-4" data-id="element-3389" />}
              <span className="hidden sm:inline" data-id="element-3390">Actualizar</span>
            </button>
          </div>
        </div>
      </header>

      {/* ─── Filters Bar ────────────────────────────────── */}
      <div className="bg-[#12121a] border-b border-white/[0.06]" data-id="element-3391">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-id="element-3392">
          <div className="flex items-center gap-3" data-id="element-3393">
            <FilterIcon className="w-4 h-4 text-white/40" data-id="element-3394" />
            <div className="relative" data-id="element-3395">
              <select value={restaurantId} onChange={e => setRestaurantId(e.target.value)} className="appearance-none bg-white/[0.06] border border-white/[0.1] rounded-lg pl-3 pr-8 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors" data-id="element-3396">
                <option value="all" data-id="element-3397">Todos los restaurantes</option>
                {restaurants.map(r => <option key={r.id} value={r.id} data-id="element-3398">
                    {r.name}
                  </option>)}
              </select>
              <ChevronDownIcon className="w-4 h-4 text-white/40 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-3399" />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap pb-1 sm:pb-0" data-id="element-3400">
            <CalendarIcon className="w-4 h-4 text-white/40 mr-1" data-id="element-3401" />
            {[{
            id: 'all',
            label: 'Todo'
          }, {
            id: 'today',
            label: 'Hoy'
          }, {
            id: '7d',
            label: '7 días'
          }, {
            id: '30d',
            label: '30 días'
          }, {
            id: 'month',
            label: 'Mes actual'
          }].map(preset => <button key={preset.id} onClick={() => handleDatePreset(preset.id)} className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${datePreset === preset.id ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/[0.03] text-white/60 border border-white/[0.05] hover:bg-white/[0.08]'}`} data-id="element-3402">
                {preset.label}
              </button>)}

            <div className="h-5 w-px bg-white/10 mx-1" data-id="element-3403" />

            <div className="flex items-center gap-2" data-id="element-3404">
              <label className="text-xs text-white/40" data-id="element-3405">Desde</label>
              <input type="date" value={dateRange.from} onChange={e => {
              setDatePreset('custom');
              setDateRange(prev => ({
                ...prev,
                from: e.target.value
              }));
            }} className="bg-white/[0.06] border border-white/[0.1] rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors [color-scheme:dark]" data-id="element-3406" />
              <label className="text-xs text-white/40" data-id="element-3407">Hasta</label>
              <input type="date" value={dateRange.to} onChange={e => {
              setDatePreset('custom');
              setDateRange(prev => ({
                ...prev,
                to: e.target.value
              }));
            }} className="bg-white/[0.06] border border-white/[0.1] rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors [color-scheme:dark]" data-id="element-3408" />
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-id="element-3409">
        {error && <div className="rounded-2xl bg-red-500/10 border border-red-500/20 p-4 text-sm text-red-400 flex items-center gap-3" data-id="element-3410">
            <AlertTriangleIcon className="w-5 h-5" data-id="element-3411" />
            {error}
          </div>}

        {loading ? <div className="space-y-8" data-id="element-3412">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-3413">
              {Array.from({
            length: 6
          }).map((_, i) => <div key={i} className="h-32 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-3414" />)}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-3415">
              <div className="lg:col-span-2 h-80 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-3416" />
              <div className="h-80 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-3417" />
            </div>
          </div> : <>
            {/* ─── KPI Cards ──────────────────────────── */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-3418">
              <KpiCard label="GMV Total" value={formatCompactCurrency(Number(data.gmv.total_gmv ?? 0))} subtitle="Ingresos brutos" icon={<TrendingUpIcon className="w-5 h-5 text-emerald-400" data-id="element-3420" />} accentColor="#10b981" data-id="element-3419" />
              <KpiCard label="Órdenes" value={Number(data.gmv.total_orders ?? 0).toLocaleString()} subtitle="Total procesadas" icon={<ShoppingCartIcon className="w-5 h-5 text-blue-400" data-id="element-3422" />} accentColor="#3b82f6" data-id="element-3421" />
              <KpiCard label="Ticket Promedio" value={formatCurrency(Number(data.gmv.avg_ticket ?? 0))} subtitle="Por orden" icon={<ReceiptIcon className="w-5 h-5 text-amber-400" data-id="element-3424" />} accentColor="#f59e0b" data-id="element-3423" />
              <KpiCard label="Clientes Únicos" value={Number(data.uniqueCustomers ?? 0).toLocaleString()} subtitle="Compradores distintos" icon={<UsersIcon className="w-5 h-5 text-cyan-400" data-id="element-3426" />} accentColor="#06b6d4" data-id="element-3425" />
              <KpiCard label="Recurrentes" value={Number(data.repeatCustomers.repeat_count ?? 0).toLocaleString()} subtitle={Number(data.repeatCustomers.repeat_pct ?? 0) > 0 ? `${Math.round(Number(data.repeatCustomers.repeat_pct ?? 0))}% del total` : 'Clientes que regresan'} icon={<UserCheckIcon className="w-5 h-5 text-pink-400" data-id="element-3428" />} accentColor="#ec4899" data-id="element-3427" />
              <KpiCard label="Autoservicio" value={`${Math.round(Number(data.selfServicePct.self_service_pct ?? 0))}%`} subtitle={`Mesero: ${Math.round(Number(data.selfServicePct.waiter_pct ?? 0))}%`} icon={<StoreIcon className="w-5 h-5 text-purple-400" data-id="element-3430" />} accentColor="#8b5cf6" data-id="element-3429" />
            </div>

            {/* ─── Insights Section ───────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3431">
              <div className="rounded-2xl bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 p-4 flex items-start gap-4" data-id="element-3432">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0" data-id="element-3433">
                  <AwardIcon className="w-5 h-5 text-emerald-400" data-id="element-3434" />
                </div>
                <div data-id="element-3435">
                  <h3 className="text-sm font-semibold text-emerald-400" data-id="element-3436">
                    Mejor Día
                  </h3>
                  <p className="text-white font-medium mt-1" data-id="element-3437">
                    {insights.bestDay.day}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5" data-id="element-3438">
                    {formatCurrency(insights.bestDay.val)}
                  </p>
                </div>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 p-4 flex items-start gap-4" data-id="element-3439">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0" data-id="element-3440">
                  <ClockIcon className="w-5 h-5 text-blue-400" data-id="element-3441" />
                </div>
                <div data-id="element-3442">
                  <h3 className="text-sm font-semibold text-blue-400" data-id="element-3443">
                    Hora Pico
                  </h3>
                  <p className="text-white font-medium mt-1" data-id="element-3444">
                    {insights.bestHour.hour}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5" data-id="element-3445">
                    {formatCurrency(insights.bestHour.val)}
                  </p>
                </div>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20 p-4 flex items-start gap-4" data-id="element-3446">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0" data-id="element-3447">
                  <StoreIcon className="w-5 h-5 text-amber-400" data-id="element-3448" />
                </div>
                <div data-id="element-3449">
                  <h3 className="text-sm font-semibold text-amber-400" data-id="element-3450">
                    Top Restaurante
                  </h3>
                  <p className="text-white font-medium mt-1 truncate max-w-[150px]" data-id="element-3451">
                    {insights.bestRest?.restaurant_name || '-'}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5" data-id="element-3452">
                    {insights.bestRest ? formatCurrency(insights.bestRest.gmv) : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* ─── Charts Row 1 ───────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-3453">
              {/* GMV Daily Chart */}
              <div className="lg:col-span-2 rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-3454">
                <div className="flex items-center justify-between mb-6" data-id="element-3455">
                  <div data-id="element-3456">
                    <h2 className="text-sm font-semibold text-white/80" data-id="element-3457">
                      GMV Diario
                    </h2>
                    <p className="text-xs text-white/30 mt-0.5" data-id="element-3458">
                      Ingresos brutos por día
                    </p>
                  </div>
                </div>
                {dailyChartData.length === 0 ? <div className="h-64 flex items-center justify-center text-white/20 text-sm" data-id="element-3459">
                    Sin datos
                  </div> : <div className="h-64" data-id="element-3460">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-3461">
                      <AreaChart data={dailyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-3462">
                        <defs data-id="element-3463">
                          <linearGradient id="gmvGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-3464">
                            <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} data-id="element-3465" />
                            <stop offset="100%" stopColor="#10b981" stopOpacity={0} data-id="element-3466" />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-3467" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} interval="preserveStartEnd" data-id="element-3468" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-3469" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-3471" />} data-id="element-3470" />
                        <Area type="monotone" dataKey="gmv" name="GMV" stroke="#10b981" strokeWidth={2} fill="url(#gmvGrad)" data-id="element-3472" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Channel Distribution */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-3473">
                <div className="mb-4" data-id="element-3474">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-3475">
                    Canales
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-3476">
                    Distribución de pedidos
                  </p>
                </div>
                {channelChartData.length === 0 ? <div className="h-48 flex items-center justify-center text-white/20 text-sm" data-id="element-3477">
                    Sin datos
                  </div> : <>
                    <div className="h-48" data-id="element-3478">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-3479">
                        <PieChart data-id="element-3480">
                          <Pie data={channelChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value" strokeWidth={0} data-id="element-3481">
                            {channelChartData.map((entry, i) => <Cell key={i} fill={entry.color} data-id="element-3482" />)}
                          </Pie>
                          <Tooltip content={({
                      active,
                      payload
                    }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      const pct = totalChannelOrders > 0 ? Math.round(d.value / totalChannelOrders * 100) : 0;
                      return <div className="bg-white/10 backdrop-blur-xl border border-white/10 text-white px-3 py-2 rounded-xl shadow-2xl text-sm" data-id="element-3484">
                                  <p className="font-medium" data-id="element-3485">{d.name}</p>
                                  <p className="text-white/50" data-id="element-3486">
                                    {Number(d.value ?? 0).toLocaleString()}{' '}
                                    pedidos ({pct}%)
                                  </p>
                                </div>;
                    }} data-id="element-3483" />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2 mt-2" data-id="element-3487">
                      {channelChartData.map(entry => {
                  const pct = totalChannelOrders > 0 ? Math.round(entry.value / totalChannelOrders * 100) : 0;
                  return <div key={entry.name} className="flex items-center justify-between" data-id="element-3488">
                            <div className="flex items-center gap-2" data-id="element-3489">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                        backgroundColor: entry.color
                      }} data-id="element-3490" />
                              <span className="text-sm text-white/60" data-id="element-3491">
                                {entry.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-3" data-id="element-3492">
                              <span className="text-sm font-semibold text-white/90" data-id="element-3493">
                                {Number(entry.value ?? 0).toLocaleString()}
                              </span>
                              <span className="text-xs text-white/30 w-10 text-right" data-id="element-3494">
                                {pct}%
                              </span>
                            </div>
                          </div>;
                })}
                    </div>
                  </>}
              </div>
            </div>

            {/* ─── Charts Row 2 ───────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-3495">
              {/* Hourly Sales */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-3496">
                <div className="mb-6" data-id="element-3497">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-3498">
                    Ventas por Hora
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-3499">
                    Distribución a lo largo del día
                  </p>
                </div>
                {hourlyChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-3500">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-3501">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-3502">
                      <BarChart data={hourlyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-3503">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-3504" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} data-id="element-3505" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-3506" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-3508" />} data-id="element-3507" />
                        <Bar dataKey="gmv" name="GMV" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={32} opacity={0.8} data-id="element-3509" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Orders Daily */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-3510">
                <div className="mb-6" data-id="element-3511">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-3512">
                    Órdenes Diarias
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-3513">
                    Volumen de pedidos por día
                  </p>
                </div>
                {dailyChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-3514">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-3515">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-3516">
                      <BarChart data={dailyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-3517">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-3518" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} interval="preserveStartEnd" data-id="element-3519" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} data-id="element-3520" />
                        <Tooltip content={<DarkTooltip isCurrency={false} data-id="element-3522" />} data-id="element-3521" />
                        <Bar dataKey="orders" name="Órdenes" fill="#8b5cf6" radius={[3, 3, 0, 0]} maxBarSize={32} opacity={0.8} data-id="element-3523" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>
            </div>

            {/* ─── Charts Row 3 (Horizontal Bars) ──────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-3524">
              {/* Top Restaurants */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-3525">
                <div className="mb-6" data-id="element-3526">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-3527">
                    Top Restaurantes
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-3528">
                    Por GMV generado
                  </p>
                </div>
                {topRestaurantsChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-3529">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-3530">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-3531">
                      <BarChart data={topRestaurantsChartData} layout="vertical" margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0
                }} data-id="element-3532">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} data-id="element-3533" />
                        <XAxis type="number" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-3534" />
                        <YAxis dataKey="name" type="category" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.6)'
                  }} tickLine={false} axisLine={false} width={100} data-id="element-3535" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-3537" />} data-id="element-3536" />
                        <Bar dataKey="gmv" name="GMV" fill="#f59e0b" radius={[0, 3, 3, 0]} barSize={20} opacity={0.8} data-id="element-3538" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Top Products */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-3539">
                <div className="mb-6" data-id="element-3540">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-3541">
                    Top Productos
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-3542">
                    Por ingresos generados
                  </p>
                </div>
                {topProductsChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-3543">
                    Sin datos
                  </div> : <div className="h-56" data-id="element-3544">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-3545">
                      <BarChart data={topProductsChartData} layout="vertical" margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0
                }} data-id="element-3546">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} data-id="element-3547" />
                        <XAxis type="number" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={formatCompactCurrency} data-id="element-3548" />
                        <YAxis dataKey="name" type="category" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.6)'
                  }} tickLine={false} axisLine={false} width={100} data-id="element-3549" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-3551" />} data-id="element-3550" />
                        <Bar dataKey="revenue" name="Ingresos" fill="#ec4899" radius={[0, 3, 3, 0]} barSize={20} opacity={0.8} data-id="element-3552" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>
            </div>

            {/* ─── Tables Section ─────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-3553">
              {/* Top Products Table */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] overflow-hidden flex flex-col" data-id="element-3554">
                <div className="p-5 border-b border-white/[0.06]" data-id="element-3555">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-3556">
                    Detalle de Productos
                  </h2>
                </div>
                <div className="overflow-x-auto flex-1" data-id="element-3557">
                  <table className="w-full text-sm" data-id="element-3558">
                    <thead data-id="element-3559">
                      <tr className="text-xs text-white/30 uppercase tracking-wider bg-white/[0.02]" data-id="element-3560">
                        <th className="text-left px-5 py-3 font-medium" data-id="element-3561">
                          Producto
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-3562">
                          Cant.
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-3563">
                          Ingresos
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-3564">
                      {data.topProducts.slice(0, 10).map((p, i) => <tr key={i} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-id="element-3565">
                          <td className="px-5 py-3" data-id="element-3566">
                            <p className="font-medium text-white/90" data-id="element-3567">
                              {p.product_name}
                            </p>
                            {restaurantId === 'all' && <p className="text-xs text-white/40 mt-0.5" data-id="element-3568">
                                {p.restaurant_name}
                              </p>}
                          </td>
                          <td className="px-5 py-3 text-right text-white/60" data-id="element-3569">
                            {p.quantity}
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-emerald-400" data-id="element-3570">
                            {formatCurrency(p.revenue)}
                          </td>
                        </tr>)}
                      {data.topProducts.length === 0 && <tr data-id="element-3571">
                          <td colSpan={3} className="px-5 py-8 text-center text-white/30" data-id="element-3572">
                            Sin datos
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Recent Orders Table */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] overflow-hidden flex flex-col" data-id="element-3573">
                <div className="p-5 border-b border-white/[0.06]" data-id="element-3574">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-3575">
                    Órdenes Recientes
                  </h2>
                </div>
                <div className="overflow-x-auto flex-1" data-id="element-3576">
                  <table className="w-full text-sm" data-id="element-3577">
                    <thead data-id="element-3578">
                      <tr className="text-xs text-white/30 uppercase tracking-wider bg-white/[0.02]" data-id="element-3579">
                        <th className="text-left px-5 py-3 font-medium" data-id="element-3580">
                          ID / Cliente
                        </th>
                        <th className="text-left px-5 py-3 font-medium" data-id="element-3581">
                          Estado
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-3582">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-3583">
                      {data.recentOrders.slice(0, 10).map((o, i) => <tr key={o.id || i} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-id="element-3584">
                          <td className="px-5 py-3" data-id="element-3585">
                            <p className="font-medium text-white/90" data-id="element-3586">
                              {String(o.id).substring(0, 8).toUpperCase()}
                            </p>
                            <p className="text-xs text-white/40 mt-0.5" data-id="element-3587">
                              {o.customer_name}
                            </p>
                          </td>
                          <td className="px-5 py-3" data-id="element-3588">
                            <span className="px-2 py-1 rounded-md text-[10px] font-medium uppercase tracking-wider bg-white/[0.06] text-white/70" data-id="element-3589">
                              {o.status}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-white" data-id="element-3590">
                            {formatCurrency(o.total)}
                          </td>
                        </tr>)}
                      {data.recentOrders.length === 0 && <tr data-id="element-3591">
                          <td colSpan={3} className="px-5 py-8 text-center text-white/30" data-id="element-3592">
                            Sin datos
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Quick Links Section */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3593">
              <Link to="/admin/enrollment/receipts" className="bg-[#12121a] border border-white/[0.06] rounded-2xl p-6 hover:bg-white/[0.02] transition-colors group flex items-center justify-between" data-id="element-3594">
                <div className="flex items-center gap-4" data-id="element-3595">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-3596">
                    <FileTextIcon className="w-6 h-6 text-emerald-400" data-id="element-3597" />
                  </div>
                  <div data-id="element-3598">
                    <h3 className="text-white font-bold" data-id="element-3599">Recibos de Pago</h3>
                    <p className="text-sm text-white/50 mt-0.5" data-id="element-3600">
                      Generar y consultar recibos
                    </p>
                  </div>
                </div>
                <ArrowLeftIcon className="w-5 h-5 text-white/30 rotate-180 group-hover:text-white/70 transition-colors" data-id="element-3601" />
              </Link>
            </div>
          </>}
      </main>
    </div>;
}